import { useState } from 'react'
import { Box } from '@mui/material'
import { L } from '../ui/LavDropdown'
import { COLOR_OPTIONS } from '../ServiceEditorPanel'

/**
 * Shared primitives for the Services feature, from the Claude Design
 * "Services.html" spec (SvIc / SvDD / ServiceFormFields). The form enforces
 * that an item is EITHER a bookable service OR an add-on (never both) and
 * swaps the category options per type — categories are split the same way.
 */

// ---- icon set (design glyphs) ----
const PATHS = {
	search: (
		<>
			<circle cx="7" cy="7" r="4.6" />
			<path d="M10.4 10.4L14 14" />
		</>
	),
	plus: <path d="M8 3v10M3 8h10" />,
	back: <path d="M10 3.5L5.5 8l4.5 4.5" />,
	chev: <path d="M6 3.5L10.5 8L6 12.5" />,
	pencil: <path d="M11.5 2.5l2 2L6 12l-2.6.6L4 10z" />,
	trash: <path d="M3 4.5h10M6.5 4V3h3v1M5 4.5l.5 8h5l.5-8" />,
	check: <path d="M3 8.5l3.5 3.5L13 4.5" />,
	down: <path d="M4 6l4 4 4-4" />,
	up: <path d="M4 10l4-4 4 4" />,
	x: <path d="M4 4l8 8M12 4l-8 8" />,
	layers: (
		<>
			<path d="M8 2.2L14 5 8 7.8 2 5z" />
			<path d="M2 8.2l6 2.8 6-2.8M2 11.2l6 2.8 6-2.8" />
		</>
	),
	grip: (
		<>
			<circle cx="6" cy="3.6" r="1.05" fill="currentColor" stroke="none" />
			<circle cx="10" cy="3.6" r="1.05" fill="currentColor" stroke="none" />
			<circle cx="6" cy="8" r="1.05" fill="currentColor" stroke="none" />
			<circle cx="10" cy="8" r="1.05" fill="currentColor" stroke="none" />
			<circle cx="6" cy="12.4" r="1.05" fill="currentColor" stroke="none" />
			<circle cx="10" cy="12.4" r="1.05" fill="currentColor" stroke="none" />
		</>
	),
}

export function SvIc({ name, size = 16, sw = 1.6 }) {
	return (
		<svg
			width={size}
			height={size}
			viewBox="0 0 16 16"
			fill="none"
			stroke="currentColor"
			strokeWidth={sw}
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
			style={{ flex: 'none' }}
		>
			{PATHS[name] || null}
		</svg>
	)
}

// ---- color helpers (appointment_color named values → hex via COLOR_OPTIONS) ----
export const svColorHex = (value) =>
	COLOR_OPTIONS.find((o) => o.value === value && o.value !== '')?.color || null

export const svColorLabel = (value) => {
	if (!value) return 'None'
	return COLOR_OPTIONS.find((o) => o.value === value)?.label || value
}

export const svSwatchSx = (value) => {
	const hex = svColorHex(value)
	return hex
		? { bgcolor: hex }
		: { bgcolor: '#EDF0F5', border: '1px solid #D8DCE6' }
}

export const svDotSx = (value) => ({
	width: 12, height: 12, flex: 'none', borderRadius: '4px', ...svSwatchSx(value),
})

/** Sentinel value for the category dropdown's "New category…" action row. */
export const NEW_CAT = '__new'

// ---- dropdown primitive (sv-dd) ----
export function SvDD({ label, empty, options, onPick, tall = false }) {
	const [open, setOpen] = useState(false)
	return (
		<Box sx={{ position: 'relative', minWidth: 0 }}>
			<Box
				component="button"
				type="button"
				onClick={() => setOpen((o) => !o)}
				sx={{
					display: 'flex', alignItems: 'center', gap: '9px', width: '100%',
					height: tall ? 46 : 44, border: `1.5px solid ${open ? L.primary : L.border}`,
					borderRadius: '10px', bgcolor: '#FFFFFF', p: '0 12px',
					fontSize: tall ? '15px' : '14px', fontWeight: 600, color: L.text,
					fontFamily: 'inherit', cursor: 'pointer',
					boxShadow: open ? `0 0 0 3px ${L.primarySoft}` : 'none',
					'&:hover': { borderColor: L.primary },
				}}
			>
				<Box component="span" sx={{
					flex: 1, minWidth: 0, textAlign: 'left', overflow: 'hidden',
					textOverflow: 'ellipsis', whiteSpace: 'nowrap',
					...(empty ? { color: '#B9B2CC', fontWeight: 500 } : {}),
				}}>
					{label}
				</Box>
				<Box sx={{ display: 'flex', color: L.muted, flex: 'none' }}>
					<SvIc name="down" size={13} sw={1.8} />
				</Box>
			</Box>
			{open && (
				<>
					<Box onClick={() => setOpen(false)} sx={{ position: 'fixed', inset: 0, zIndex: 55 }} />
					<Box sx={{
						position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 60,
						bgcolor: '#FFFFFF', border: `1px solid ${L.border}`, borderRadius: '12px',
						p: '5px', display: 'flex', flexDirection: 'column', gap: '2px',
						maxHeight: 264, overflowY: 'auto',
						boxShadow: '0 14px 36px rgba(95,78,194,0.16), 0 3px 10px rgba(95,78,194,0.08)',
					}}>
						{options.map((o, i) => (
							<Box
								component="button"
								type="button"
								key={i}
								onClick={() => { onPick(o.v); setOpen(false) }}
								sx={{
									display: 'flex', alignItems: 'center', gap: '9px', flex: 'none',
									p: '9px 10px', border: 'none', borderRadius: '8px',
									bgcolor: o.on ? L.primarySoft : 'transparent',
									fontFamily: 'inherit', fontSize: '13.5px', fontWeight: 600,
									color: o.on ? L.primaryDeep : L.text, textAlign: 'left', width: '100%',
									cursor: 'pointer',
									'&:hover': { bgcolor: o.on ? L.primarySoft : L.surface },
									// Trailing "action" item (e.g. New category…) — separated from
									// the value list by a rule, per the sv-dd-item.add spec.
									...(o.add ? {
										mt: '5px', pt: '11px', borderTop: `1px solid ${L.border}`,
										borderRadius: '0 0 8px 8px', color: L.primaryDeep,
										'&:hover': { bgcolor: L.primarySoft },
									} : {}),
								}}
							>
								{o.node}
								{o.on && (
									<Box sx={{ ml: 'auto', display: 'flex', color: L.primaryDeep, flex: 'none' }}>
										<SvIc name="check" size={13} sw={2.2} />
									</Box>
								)}
							</Box>
						))}
					</Box>
				</>
			)}
		</Box>
	)
}

// ---- form state ----
export const svEmptyForm = (preset = {}) => ({
	name: '', color: '', catId: null, mins: '', price: '', desc: '', type: 'service',
	...preset,
})

export const svFormFromService = (s) => ({
	name: s.name || '',
	color: s.appointment_color || '',
	catId: s.category_id || null,
	mins: s.duration_minutes != null ? String(s.duration_minutes) : '',
	price: s.price != null ? (s.price / 100).toFixed(2) : '',
	desc: s.description || '',
	// Enforced single role: an item is an add-on or a service, never both.
	type: s.is_add_on ? 'addon' : 'service',
})

/**
 * Smallest duration an item of this type may have. Add-ons can be 0 minutes
 * (cost but no extra time on the appointment); bookable services can't, since
 * the appointment's end time is derived from the duration. Mirrors the
 * service_types_duration_nonneg_check constraint in the database.
 */
export const svMinMins = (type) => (type === 'addon' ? 0 : 5)

export const svFormValid = (f) => {
	const mins = parseInt(f.mins, 10)
	const price = parseFloat(f.price)
	return (
		f.name.trim().length > 0 &&
		!isNaN(mins) &&
		mins >= svMinMins(f.type) &&
		!isNaN(price) &&
		price >= 0
	)
}

/** DB payload from form. Type is exclusive: service XOR add-on. */
export const svPayloadFromForm = (f, merchantId) => ({
	merchant_id: merchantId,
	name: f.name.trim(),
	duration_minutes: parseInt(f.mins, 10),
	category_id: f.catId || null,
	appointment_color: f.color || null,
	price: Math.round(parseFloat(f.price) * 100),
	description: f.desc.trim() || null,
	is_service: f.type === 'service',
	is_add_on: f.type === 'addon',
})

const lblSx = (tall) => ({ fontSize: tall ? '13px' : '12.5px', fontWeight: 650, color: L.text })

const inputSx = (tall) => ({
	width: '100%', height: tall ? 46 : 44, border: `1.5px solid ${L.border}`,
	borderRadius: '10px', bgcolor: '#FFFFFF', p: '0 13px', fontFamily: 'inherit',
	fontSize: tall ? '15px' : '14px', color: L.text, outline: 'none',
	'&:focus': { borderColor: L.primary, boxShadow: `0 0 0 3px ${L.primarySoft}` },
	'&::placeholder': { color: '#B9B2CC', fontWeight: 500 },
})

/**
 * Shared Add/Edit fields (desktop drawer + mobile screen). The Type toggle is
 * a mutually-exclusive pair — switching type clears the category since
 * service categories and add-on categories are separate lists.
 */
export function ServiceFormFields({ form, setField, catsByType, currencySymbol, onManageCats, tall = false }) {
	const minMins = svMinMins(form.type)
	const bumpMins = (d) => {
		const cur = parseInt(form.mins, 10) || 0
		setField('mins', String(Math.max(minMins, cur + d)))
	}
	const catOpts = form.type === 'addon' ? catsByType.addon : catsByType.service
	// Resolve against the live list so a category renamed/deleted from the
	// manage surface is reflected here without remounting the form.
	const selectedCat = form.catId ? catOpts.find((c) => c.id === form.catId) : null
	const fSx = { display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 }
	const req = <Box component="em" sx={{ color: '#D96A6A', fontStyle: 'normal', fontWeight: 750 }}>*</Box>

	return (
		<>
			{/* Type — exclusive service / add-on */}
			<Box sx={{ display: 'flex', alignItems: 'center', gap: '18px', p: '2px 0 0' }}>
				<Box component="span" sx={{ ...lblSx(tall), mr: '2px' }}>Type</Box>
				{[['service', 'Service'], ['addon', 'Add-on']].map(([v, label]) => (
					<Box
						component="button"
						type="button"
						key={v}
						role="checkbox"
						aria-checked={form.type === v}
						onClick={() => {
							if (form.type !== v) {
								setField('type', v)
								// Category lists are per-type — clear on switch.
								setField('catId', null)
								// 0 minutes is only legal for add-ons; don't leave a
								// service sitting on a duration it can't be saved with.
								if (v === 'service' && parseInt(form.mins, 10) === 0) setField('mins', '')
							}
						}}
						sx={{
							display: 'inline-flex', alignItems: 'center', gap: '9px', border: 'none',
							bgcolor: 'transparent', p: '4px 2px', fontSize: tall ? '15px' : '14px',
							fontWeight: 650, color: L.text, borderRadius: '8px', fontFamily: 'inherit',
							cursor: 'pointer',
						}}
					>
						<Box sx={{
							width: 22, height: 22, flex: 'none', borderRadius: '6px',
							border: `1.5px solid ${form.type === v ? L.primary : '#D4CDE6'}`,
							bgcolor: form.type === v ? L.primary : '#FFFFFF',
							display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FFFFFF',
							transition: 'background .12s, border-color .12s',
						}}>
							{form.type === v && <SvIc name="check" size={12} sw={2.6} />}
						</Box>
						<span>{label}</span>
					</Box>
				))}
			</Box>

			<Box sx={fSx}>
				<Box component="span" sx={lblSx(tall)}>
					{form.type === 'addon' ? 'Add-on' : 'Service'} name {req}
				</Box>
				<Box
					component="input"
					type="text"
					placeholder={form.type === 'addon' ? 'e.g. Teeth Brushing' : 'e.g. Dog Grooming'}
					value={form.name}
					onChange={(e) => setField('name', e.target.value)}
					sx={inputSx(tall)}
				/>
			</Box>

			<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
				<Box sx={fSx}>
					<Box component="span" sx={lblSx(tall)}>Color</Box>
					<SvDD
						tall={tall}
						label={
							<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '9px', minWidth: 0 }}>
								<Box sx={{ width: 14, height: 14, flex: 'none', borderRadius: '4px', ...svSwatchSx(form.color) }} />
								<span>{svColorLabel(form.color)}</span>
							</Box>
						}
						onPick={(v) => setField('color', v)}
						options={COLOR_OPTIONS.map((o) => ({
							v: o.value,
							on: (form.color || '') === o.value,
							node: (
								<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '9px', minWidth: 0 }}>
									<Box sx={{ width: 14, height: 14, flex: 'none', borderRadius: '4px', ...svSwatchSx(o.value) }} />
									<span>{o.label}</span>
								</Box>
							),
						}))}
					/>
				</Box>
				<Box sx={fSx}>
					<Box component="span" sx={lblSx(tall)}>Category</Box>
					<SvDD
						tall={tall}
						empty={!selectedCat}
						label={selectedCat ? selectedCat.name : 'Select category'}
						onPick={(v) => {
							// Sentinel: hand off to the manage-categories surface rather
							// than setting a value.
							if (v === NEW_CAT) {
								if (onManageCats) onManageCats(form.type)
								return
							}
							setField('catId', v || null)
						}}
						options={[
							{ v: '', on: !selectedCat, node: <Box component="span" sx={{ color: L.muted }}>No category</Box> },
							...catOpts.map((c) => ({ v: c.id, on: form.catId === c.id, node: c.name })),
							...(onManageCats ? [{
								v: NEW_CAT,
								add: true,
								node: (
									<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '7px', minWidth: 0 }}>
										<SvIc name="plus" size={13} sw={2.2} />
										<span>New category…</span>
									</Box>
								),
							}] : []),
						]}
					/>
				</Box>
			</Box>

			<Box sx={fSx}>
				<Box component="span" sx={lblSx(tall)}>Duration (minutes) {req}</Box>
				<Box sx={{ position: 'relative' }}>
					<Box
						component="input"
						type="text"
						inputMode="numeric"
						placeholder={form.type === 'addon' ? 'e.g. 15 — use 0 for no extra time' : 'e.g. 60'}
						value={form.mins}
						onChange={(e) => setField('mins', e.target.value.replace(/[^\d]/g, ''))}
						sx={{ ...inputSx(tall), pr: '44px' }}
					/>
					<Box sx={{
						position: 'absolute', right: 7, top: '50%', transform: 'translateY(-50%)',
						display: 'flex', flexDirection: 'column', gap: '1px',
					}}>
						{[['up', 5], ['down', -5]].map(([ic, d]) => (
							<Box
								component="button"
								type="button"
								key={ic}
								tabIndex={-1}
								aria-label={d > 0 ? 'Increase duration' : 'Decrease duration'}
								onClick={() => bumpMins(d)}
								sx={{
									width: 24, height: 15, border: 'none', borderRadius: '5px',
									bgcolor: 'transparent', color: L.muted, display: 'flex',
									alignItems: 'center', justifyContent: 'center', p: 0, cursor: 'pointer',
									'&:hover': { bgcolor: L.surface, color: L.text },
								}}
							>
								<SvIc name={ic} size={10} sw={2.4} />
							</Box>
						))}
					</Box>
				</Box>
			</Box>

			<Box sx={fSx}>
				<Box component="span" sx={lblSx(tall)}>Price {currencySymbol} {req}</Box>
				<Box
					component="input"
					type="text"
					inputMode="decimal"
					placeholder="e.g. 50.00"
					value={form.price}
					onChange={(e) => setField('price', e.target.value.replace(/[^\d.]/g, ''))}
					sx={inputSx(tall)}
				/>
			</Box>

			<Box sx={fSx}>
				<Box component="span" sx={lblSx(tall)}>Description</Box>
				<Box
					component="textarea"
					placeholder="Describe this service…"
					value={form.desc}
					onChange={(e) => setField('desc', e.target.value)}
					sx={{
						...inputSx(tall),
						height: 'auto', minHeight: tall ? 100 : 96, p: '11px 13px',
						resize: 'vertical', lineHeight: 1.5,
					}}
				/>
			</Box>
		</>
	)
}
